// Shared cell-value library: every step reads and compares values through
// these helpers so blanks, numbers, dates and text behave the same everywhere.
import { format as formatDateFns, isValid, parse } from "date-fns";

export const COLUMN_TYPES = ["text", "number", "date", "boolean"];

export const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "") ||
  (typeof value === "number" && Number.isNaN(value));

export const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

// ExcelJS stores dates as UTC instants for the calendar date; rebuild them
// as local dates so the day never shifts in negative-offset timezones.
const fromExcelDate = (date) =>
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );

export const toExcelDate = (date) =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ),
  );

// Flattens an ExcelJS cell value (rich text, hyperlink, formula, error)
// into a plain value. Strings are trimmed and blank strings become null.
export const normalizeCell = (value) => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return isValidDate(value) ? fromExcelDate(value) : null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  }
  if (typeof value !== "object") return value;
  if (Array.isArray(value.richText)) {
    return normalizeCell(value.richText.map((part) => part.text ?? "").join(""));
  }
  if ("formula" in value || "sharedFormula" in value) return normalizeCell(value.result);
  if ("result" in value) return normalizeCell(value.result);
  if ("text" in value) return normalizeCell(value.text);
  if ("error" in value) return null;
  return String(value);
};

// ---------- Numbers ----------

// Optional currency/unit text around a number: "$1,200.50", "USD 100",
// "(500)", "500-", "12%".
const NUMBER_PATTERN = /^([^\d\-+(.,]*?)\s*(\()?\s*([-+])?\s*([\d.,\s']+?)\s*(-)?\s*(\))?\s*([^\d]*?)$/;

// Text allowed around a number: currency symbols ($, €, ₹), 3-letter codes
// (USD), short codes joined to a symbol (US$, HK$) and Rs. Anything else
// ("Description 1", "Visa 0919") makes the value text, not a number.
const CURRENCY_AFFIX = /^(?:\p{Sc}+|[A-Z]{3}|[A-Z]{1,3}\p{Sc}|Rs)$/u;
const isCurrencyAffix = (text) => text === "" || CURRENCY_AFFIX.test(text);

const parseDigits = (digits) => {
  const compact = digits.replace(/[\s']/g, "");
  const lastDot = compact.lastIndexOf(".");
  const lastComma = compact.lastIndexOf(",");
  let normalized;

  if (lastDot !== -1 && lastComma !== -1) {
    // Whichever separator comes last is the decimal separator.
    normalized =
      lastComma > lastDot
        ? compact.replace(/\./g, "").replace(",", ".")
        : compact.replace(/,/g, "");
  } else if (lastComma !== -1) {
    // "1,234" / "1,234,567" are thousands; "12,5" is a decimal comma.
    normalized = /^\d{1,3}(,\d{3})+$/.test(compact)
      ? compact.replace(/,/g, "")
      : compact.replace(",", ".");
  } else {
    normalized = /^\d{1,3}(\.\d{3}){2,}$/.test(compact) ? compact.replace(/\./g, "") : compact;
  }

  return /^\d*\.?\d+$|^\d+\.$/.test(normalized) ? parseFloat(normalized) : null;
};

// Returns a finite number or null. Never guesses digits out of mixed text
// like "12abc34".
export const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (value instanceof Date) return null;

  // "Rs." would otherwise read its dot as a decimal point.
  const text = String(value).trim().replace(/^Rs\.\s*/, "Rs ");
  if (!text) return null;

  const match = text.match(NUMBER_PATTERN);
  if (!match) return null;
  const [, prefix, openParen, sign, digits, trailingMinus, closeParen, suffix] = match;
  if (Boolean(openParen) !== Boolean(closeParen)) return null;
  const unit = suffix.trim().replace(/%$/, "").trim();
  if (!isCurrencyAffix(prefix.trim()) || !isCurrencyAffix(unit)) return null;

  let number = parseDigits(digits);
  if (number === null) return null;
  if (suffix.trim() === "%") number /= 100;
  const negative = Boolean(openParen) || sign === "-" || Boolean(trailingMinus);
  return negative ? -number : number;
};

// ---------- Dates ----------

const MONTHS = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};

const expandYear = (year) => {
  if (year >= 100) return year;
  return year < 50 ? 2000 + year : 1900 + year;
};

const buildDate = (year, monthIndex, day, time = [0, 0, 0]) => {
  if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return null;
  const date = new Date(expandYear(year), monthIndex, day, ...time);
  // Reject roll-overs such as 31/02.
  return date.getMonth() === monthIndex && date.getDate() === day ? date : null;
};

const TIME_PATTERN = /[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*([AaPp][Mm])?\s*(Z|[+-]\d{2}:?\d{2})?$/;

const splitTime = (text) => {
  const match = text.match(TIME_PATTERN);
  if (!match) return { datePart: text, time: [0, 0, 0], zoned: false };
  let hours = Number(match[1]);
  const meridiem = match[4]?.toLowerCase();
  if (meridiem === "pm" && hours < 12) hours += 12;
  if (meridiem === "am" && hours === 12) hours = 0;
  return {
    datePart: text.slice(0, match.index).trim(),
    time: [hours, Number(match[2]), Number(match[3] || 0)],
    zoned: Boolean(match[5]),
  };
};

const parseNumericDate = (text, dateOrder) => {
  const compact = text.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compact) return [Number(compact[1]), Number(compact[2]) - 1, Number(compact[3])];

  const parts = text.match(/^(\d{1,4})[/\-. ](\d{1,2})[/\-. ](\d{1,4})$/);
  if (!parts) return null;
  const [a, b, c] = parts.slice(1).map(Number);

  if (parts[1].length === 4) return [a, b - 1, c]; // yyyy-MM-dd
  if (parts[3].length < 2) return null;
  // Unambiguous cases win over the preferred order.
  if (a > 12 && b <= 12) return [c, b - 1, a];
  if (b > 12 && a <= 12) return [c, a - 1, b];
  return dateOrder === "MDY" ? [c, a - 1, b] : [c, b - 1, a];
};

const parseTextMonthDate = (text) => {
  const tokens = text
    .replace(/,/g, " ")
    .split(/[\s\-/.]+/)
    .filter(Boolean);
  if (tokens.length !== 3) return null;

  const monthPosition = tokens.findIndex((token) => /^[a-z]+$/i.test(token));
  if (monthPosition === -1) return null;
  const monthIndex = MONTHS[tokens[monthPosition].slice(0, 4).toLowerCase()] ??
    MONTHS[tokens[monthPosition].slice(0, 3).toLowerCase()];
  if (monthIndex === undefined) return null;

  const numbers = tokens.filter((_, i) => i !== monthPosition);
  if (!numbers.every((token) => /^\d{1,4}$/.test(token))) return null;
  const [first, second] = numbers;
  // "2024 Jan 15" puts the year first; otherwise the year comes last.
  return first.length === 4
    ? [Number(first), monthIndex, Number(second)]
    : [Number(second), monthIndex, Number(first)];
};

// Parses a value into a local Date or returns null. `options.format` forces
// an exact date-fns pattern; otherwise common numeric and month-name formats
// are detected, using `options.dateOrder` ("DMY" | "MDY") for ambiguous ones.
export const toDate = (value, options = {}) => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return isValidDate(value) ? value : null;
  if (typeof value === "number") return options.excelSerial ? fromExcelSerial(value) : null;

  const text = String(value).trim();
  if (!text) return null;

  if (options.format) {
    const parsed = parse(text, options.format, new Date());
    return isValid(parsed) ? parsed : null;
  }

  const { datePart, time, zoned } = splitTime(text);
  if (zoned) {
    const native = new Date(text);
    return isValidDate(native) ? native : null;
  }

  const parts =
    parseNumericDate(datePart, options.dateOrder || "DMY") || parseTextMonthDate(datePart);
  return parts ? buildDate(parts[0], parts[1], parts[2], time) : null;
};

// Excel serial day numbers (1900 date system).
export const fromExcelSerial = (serial) => {
  if (!Number.isFinite(serial) || serial < 1 || serial > 2958465) return null;
  const utc = new Date(Math.round((serial - 25569) * 86400 * 1000));
  return fromExcelDate(utc);
};

export const formatDate = (date, pattern = "yyyy-MM-dd") =>
  isValidDate(date) ? formatDateFns(date, pattern) : null;

// ---------- Text ----------

const hasTime = (date) =>
  date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;

export const toText = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    return isValidDate(value) ? formatDate(value, hasTime(value) ? "yyyy-MM-dd HH:mm:ss" : "yyyy-MM-dd") : "";
  }
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  return String(value);
};

export const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  const text = toText(value).trim().toLowerCase();
  if (["true", "yes", "y", "1"].includes(text)) return true;
  if (["false", "no", "n", "0"].includes(text)) return false;
  return null;
};

// ---------- Typing & comparison ----------

export const convertValue = (value, type, options) => {
  if (isBlank(value)) return null;
  switch (type) {
    case "number":
      return toNumber(value);
    case "date":
      return toDate(value, options);
    case "boolean":
      return toBoolean(value);
    default:
      return toText(value);
  }
};

// Guesses a column type from its values: a type wins when nearly every
// non-blank value converts to it.
export const inferType = (values, { sampleSize = 200, threshold = 0.9 } = {}) => {
  const present = [];
  for (const value of values) {
    if (!isBlank(value)) present.push(value);
    if (present.length >= sampleSize) break;
  }
  if (present.length === 0) return "text";

  const share = (test) => present.filter(test).length / present.length;
  if (share((v) => typeof v === "boolean") >= threshold) return "boolean";
  if (share((v) => toNumber(v) !== null && !(v instanceof Date)) >= threshold) return "number";
  if (share((v) => toDate(v) !== null) >= threshold) return "date";
  return "text";
};

// Sort key for a value; null means blank/unparseable (always sorted last).
export const sortKey = (value, type) => {
  if (isBlank(value)) return null;
  if (type === "number") return toNumber(value);
  if (type === "date") return toDate(value)?.getTime() ?? null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  return toText(value);
};

const textCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export const compareKeys = (a, b) => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return textCollator.compare(String(a), String(b));
};

// Loose equality used by matching/dedupe: numbers by value, dates by
// instant, text case-insensitive and trimmed.
export const valueKey = (value, { caseSensitive = false } = {}) => {
  if (isBlank(value)) return "";
  if (value instanceof Date) return `d:${value.getTime()}`;
  if (typeof value === "number") return `n:${value}`;
  const text = toText(value).trim();
  // "100" from a CSV must match 100 from an Excel file; codes with leading
  // zeros ("00123") stay text.
  if (/^-?(0|[1-9]\d*)(\.\d+)?$/.test(text)) return `n:${Number(text)}`;
  return `t:${caseSensitive ? text : text.toLowerCase()}`;
};
